import { Apple, Car, ChevronDown, Crosshair, Crown, Dices, Flag, Home, Package, Plane, RotateCcw, Skull, Sword, Swords, Tent, Trophy, Zap } from "lucide-react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "./GameFullscreenButton";
import { toast } from "./ui/sonner";
import { getCachedUser } from "../utils/auth";
import { getLinkedTag, pullVault, pushBoardVault } from "./PalsCloudSave";
import { awardFeat, awardPoints } from "../utils/rewards";
import { ART, BOARD_KEY, BUILD_PARTS, cellOf, CLAIM_COST, freshGame, GO_BONUS, loadCrowns, PIPS, rand6, SPACE_ICONS, SPACES, STARVE_COST, TOKENS, TOLL } from "./board/data";
import { diceClatter, sfx, winnerFanfare } from "./board/audio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export function ApocalypseBoard() {
  const g = useRef(freshGame());
  const [, force] = useState(0);
  const busy = useRef(false);
  const timers = useRef([]);
  const crowns = useRef(loadCrowns());
  const pushTimer = useRef(null);
  const [vaultLinked, setVaultLinked] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // weekly table pot — every game logged feeds it, Sunday's win-leader takes it all
  const [potInfo, setPotInfo] = useState(null);
  const [tableName, setTableName] = useState(() => getCachedUser()?.name || "");
  const [tableLogged, setTableLogged] = useState(null);
  const [tableBusy, setTableBusy] = useState(false);
  const bump = () => force((x) => x + 1);

  const fetchPot = () =>
    axios
      .get(`${API}/dolosway/board`)
      .then(({ data }) => setPotInfo(data))
      .catch(() => {});

  const logTable = async () => {
    const s = g.current;
    if (!s.winner) return;
    const name = tableName.trim();
    if (name.length < 2) return;
    setTableBusy(true);
    try {
      const me = s.players.find((p) => p.you);
      const { data } = await axios.post(`${API}/dolosway/score`, {
        name,
        won: !!s.winner.you,
        scrap: me ? me.scrap : 0,
        tag: getLinkedTag(),
      });
      setTableLogged(data);
      fetchPot();
    } catch {
      toast.error("SIGNAL LOST — TRY LOGGING AGAIN.");
    } finally {
      setTableBusy(false);
    }
  };

  const saveCrowns = () => {
    try {
      localStorage.setItem(BOARD_KEY, JSON.stringify(crowns.current));
    } catch {}
    const tag = getLinkedTag();
    if (!tag) return;
    window.clearTimeout(pushTimer.current);
    pushTimer.current = window.setTimeout(() => pushBoardVault(tag, { crowns: crowns.current }), 800);
  };

  useEffect(() => {
    window.__apocalypse = g;
    const timersRef = timers.current;
    fetchPot();
    // pull career crowns from the cloud vault when a Titan Tag is linked
    const tag = getLinkedTag();
    if (tag) {
      setVaultLinked(true);
      pullVault(tag)
        .then((doc) => {
          const remote = doc?.state?.board?.crowns;
          if (!remote || typeof remote !== "object") return;
          let changed = false;
          Object.entries(remote).forEach(([id, n]) => {
            if (Number.isFinite(n) && (crowns.current[id] || 0) < n) {
              crowns.current[id] = n;
              changed = true;
            }
          });
          if (changed) {
            saveCrowns();
            bump();
          }
        })
        .catch(() => {});
    }
    return () => timersRef.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushLog = (line, color = "#e8e6e3") => {
    g.current.log = [{ line, color, id: Date.now() + Math.random() }, ...g.current.log].slice(0, 9);
  };

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
  };

  const gainPart = (p, kind) => {
    p.parts[kind] += 1;
    pushLog(`${p.name} FOUND A ${kind.toUpperCase()} PART (${p.parts[kind]}/${BUILD_PARTS})`, p.color);
    if (p.parts[kind] === BUILD_PARTS) {
      p.builds.push(kind);
      p.scrap += 15;
      sfx("/assets/game-coin.wav", 0.5);
      pushLog(`${p.name} BUILT THE ${kind.toUpperCase()}! +15 SCRAP BONUS`, p.color);
    }
  };

  const raidFight = (p) => {
    const s = g.current;
    const foes = s.players.filter((r) => r.alive && r.id !== p.id);
    if (!foes.length) return;
    const foe = foes[Math.floor(Math.random() * foes.length)];
    sfx("/assets/game-slash.wav", 0.5);
    if (p.weapons > foe.weapons) {
      const steal = Math.min(20, foe.scrap);
      foe.scrap -= steal;
      p.scrap += steal;
      pushLog(`${p.name} OUTGUNS ${foe.name} — TAKES ${steal} SCRAP`, p.color);
      s.clashHit = { title: "AMBUSH ALLEY // STEEL TALKS", sub: `${p.name} OUTGUNS ${foe.name} — ${steal} SCRAP CHANGES HANDS`, color: p.color, key: Date.now() };
    } else {
      const loss = Math.min(20, p.scrap);
      p.scrap -= loss;
      foe.scrap += loss;
      pushLog(`${foe.name} OVERPOWERS ${p.name} — LOSES ${loss} SCRAP`, foe.color);
      s.clashHit = { title: "AMBUSH ALLEY // STEEL TALKS", sub: `${foe.name} OVERPOWERS ${p.name} — ${loss} SCRAP CHANGES HANDS`, color: foe.color, key: Date.now() };
    }
    later(() => { g.current.clashHit = null; bump(); }, 1700);
  };

  const drawChance = (p) => {
    const cards = [
      { icon: Package, label: "CACHE JACKPOT +25 SCRAP", apply: () => { p.scrap += 25; pushLog(`${p.name}: CACHE JACKPOT +25 SCRAP`, p.color); } },
      { icon: Skull, label: "TRAP SNARE -10 SCRAP", apply: () => { p.scrap = Math.max(0, p.scrap - 10); pushLog(`${p.name}: TRAP SNARE -10 SCRAP`, "#8c7b75"); } },
      { icon: Sword, label: "SCAVENGED A WEAPON (+1)", apply: () => { p.weapons += 1; pushLog(`${p.name}: SCAVENGED A WEAPON (+1)`, p.color); } },
      { icon: Apple, label: "FOOD RUN (+2 RATIONS)", apply: () => { p.food += 2; pushLog(`${p.name}: FOOD RUN (+2 RATIONS)`, p.color); } },
      { icon: Car, label: "SALVAGE SIGNAL — PART FOUND", apply: () => { const k = ["car", "home", "plane"][Math.floor(Math.random() * 3)]; gainPart(p, k); } },
      { icon: Swords, label: "AMBUSH SPRUNG", apply: () => raidFight(p) },
    ];
    const c = cards[Math.floor(Math.random() * cards.length)];
    c.apply();
    if (c.label !== "AMBUSH SPRUNG") {
      const s = g.current;
      s.chanceHit = { title: "WASTE CARD // FATE DEALT", sub: `${p.name} — ${c.label}`, icon: c.icon, color: p.color, key: Date.now() };
      later(() => { g.current.chanceHit = null; bump(); }, 1900);
    }
  };

  // Dolosway rule: the runner furthest ahead of p (the one in the lead)
  const hwyTarget = (p) => {
    const s = g.current;
    const others = s.players.filter((r) => r.alive && r.id !== p.id);
    if (!others.length) return null;
    const lead = others.reduce((a, b) => (b.pos > a.pos ? b : a));
    return lead.pos > p.pos ? lead : null;
  };

  // spend a weapon charge on the Dolosway: pin the leader, take +5 scrap (max 3 per game)
  const hwyFire = (p, lead) => {
    const s = g.current;
    p.weapons -= 1;
    p.hwy -= 1;
    p.scrap += 5;
    lead.stopped = true;
    sfx("/assets/game-slash.wav", 0.55);
    pushLog(`${p.name} FIRES ON THE DOLOSWAY — ${lead.name} PINNED (LOSES A TURN), +5 SCRAP TAKEN (${p.hwy} SHOT${p.hwy === 1 ? "" : "S"} LEFT)`, p.color);
    s.hwyHit = {
      title: "DOLOSWAY // SHOT ACROSS THE BOW",
      sub: `${p.name} STOPS ${lead.name} DEAD — TURN LOST · +5 SCRAP · ${p.hwy} LEFT THIS GAME`,
      color: p.color,
      key: Date.now(),
    };
    later(() => { g.current.hwyHit = null; bump(); }, 2100);
  };

  const resolveHwy = (spend) => {
    const s = g.current;
    const c = s.hwyChoice;
    if (!c) return;
    const p = s.players.find((r) => r.id === c.forId);
    const lead = s.players.find((r) => r.id === c.targetId);
    s.hwyChoice = null;
    if (spend && p && lead && p.weapons > 0 && p.hwy > 0) {
      hwyFire(p, lead);
    } else if (p) {
      pushLog(`${p.name} HOLSTERS THE IRON — SAVES A SHOT (${p.hwy} LEFT)`, "#8c7b75");
    }
    endTurn();
  };

  const resolveSpace = (p) => {
    const s = g.current;
    const sp = SPACES[p.pos];
    if (sp.t === "plot") {
      const owner = s.plots[p.pos];
      if (!owner) {
        if (p.scrap >= CLAIM_COST) {
          p.scrap -= CLAIM_COST;
          s.plots[p.pos] = p.id;
          sfx("/assets/game-coin.wav", 0.45);
          pushLog(`${p.name} CLAIMED ${sp.n} (-${CLAIM_COST} SCRAP)`, p.color);
        } else {
          pushLog(`${p.name} TOO POOR TO CLAIM ${sp.n}`, "#8c7b75");
        }
      } else if (owner !== p.id) {
        const o = s.players.find((r) => r.id === owner);
        const pay = Math.min(p.scrap, TOLL);
        p.scrap -= pay;
        o.scrap += pay;
        sfx("/assets/game-coin.wav", 0.45);
        pushLog(`${p.name} PAYS ${pay} TOLL — ${o.name} KEEPS ${sp.n}`, o.color);
      } else {
        p.scrap += 2;
        pushLog(`${p.name} HOLDS THEIR OWN ${sp.n} (+2 RATION SCRAP)`, p.color);
      }
    } else if (sp.t === "supply") {
      const r = Math.random();
      let loot;
      if (r < 0.18) { gainPart(p, "car"); loot = "CAR PART SALVAGED"; }
      else if (r < 0.36) { gainPart(p, "home"); loot = "HOME PART SALVAGED"; }
      else if (r < 0.54) { gainPart(p, "plane"); loot = "PLANE PART SALVAGED"; }
      else if (r < 0.77) { p.food += 2; pushLog(`${p.name} STOCKPILED 2 RATIONS`, p.color); loot = "+2 RATIONS STOCKPILED"; }
      else { p.weapons += 1; pushLog(`${p.name} ARMED UP (+1 WEAPON)`, p.color); loot = "+1 WEAPON ARMED UP"; }
      sfx("/assets/game-coin.wav", 0.4);
      s.supplyHit = { title: `${sp.n} // SCAVENGER'S LUCK`, sub: `${p.name} — ${loot}`, color: p.color, key: Date.now() };
      later(() => { g.current.supplyHit = null; bump(); }, 1700);
    } else if (sp.t === "chance") {
      drawChance(p);
    } else if (sp.t === "raid") {
      pushLog(`${p.name} CAUGHT IN AMBUSH ALLEY!`, "#ff9900");
      raidFight(p);
    } else if (sp.t === "worm") {
      p.scrap = Math.max(0, p.scrap - 15);
      sfx("/assets/worm-roar.wav", 0.5);
      pushLog(`THE WORM TAXES ${p.name} — 15 SCRAP DEVOURED`, "#ff0033");
      s.wormHit = {
        title: "WORM SIGN // THE SAND KING FEEDS",
        sub: `${p.name} PAYS 15 SCRAP IN FLESH-TAX`,
        color: p.color,
        key: Date.now(),
      };
      later(() => { g.current.wormHit = null; bump(); }, 1700);
    } else if (sp.t === "camp") {
      p.food += 1;
      pushLog(`${p.name} RESTS AT SAFE CAMP (+1 RATION)`, p.color);
      s.campHit = { title: "SAFE CAMP // CATCH YOUR BREATH", sub: `${p.name} RESTS — +1 RATION`, color: p.color, key: Date.now() };
      later(() => { g.current.campHit = null; bump(); }, 1600);
    } else if (sp.t === "go") {
      p.scrap += 10;
      pushLog(`${p.name} REGROUPS AT GO CAMP (+10 SCRAP)`, p.color);
      s.payHit = { title: "GO CAMP // REGROUP", sub: `${p.name} REGROUPS — +10 SCRAP`, color: p.color, key: Date.now() };
      later(() => { g.current.payHit = null; bump(); }, 1600);
    }
    // DOLOSWAY: armed runners can take the shot at the leader (3 per game)
    if (sp.n === "DOLOSWAY" && p.weapons > 0 && p.hwy > 0) {
      const lead = hwyTarget(p);
      if (lead) {
        if (p.you) {
          s.hwyChoice = { forId: p.id, targetId: lead.id, left: p.hwy, key: Date.now() };
          bump();
          return true; // turn waits on the FIRE/HOLSTER choice
        }
        if (Math.random() < 0.7) hwyFire(p, lead);
      }
    }
    return false;
  };

  const checkEnd = () => {
    const s = g.current;
    s.players.forEach((p) => {
      if (p.alive && p.scrap <= 0) {
        p.alive = false;
        Object.keys(s.plots).forEach((k) => {
          if (s.plots[k] === p.id) delete s.plots[k];
        });
        sfx("/assets/worm-roar.wav", 0.6);
        pushLog(`${p.name} STARVED OUT — PLOTS RELEASED. THE STRONGEST SURVIVE.`, "#8c7b75");
        s.deathHit = { name: p.name, color: p.color, key: Date.now() };
        later(() => { g.current.deathHit = null; bump(); }, 2400);
      }
    });
    const builder = s.players.find((p) => p.alive && p.builds.length === 3);
    const alive = s.players.filter((p) => p.alive);
    if (builder) s.winner = builder;
    else if (alive.length === 1) s.winner = alive[0];
  };

  const endTurn = () => {
    const s = g.current;
    checkEnd();
    if (s.winner) {
      crowns.current[s.winner.id] = (crowns.current[s.winner.id] || 0) + 1;
      saveCrowns();
      awardPoints("board", s.winner.you ? 10 : 3);
      if (s.winner.you) awardFeat("CROWN TAKER");
      pushLog(`${s.winner.name} TAKES THE CROWN — ${crowns.current[s.winner.id]} CAREER WIN${crowns.current[s.winner.id] > 1 ? "S" : ""}${vaultLinked ? " · VAULTED" : ""}`, "#ff9900");
    }
    bump();
    if (s.winner) {
      busy.current = false;
      sfx("/assets/game-blast.wav", 0.5);
      winnerFanfare();
      return;
    }
    const cur = s.players[s.turn];
    if (s.lastDoubles && cur.alive) {
      s.lastDoubles = false;
      pushLog(`${cur.name} ROLLED DOUBLES — EXTRA TURN!`, "#ff9900");
      busy.current = false;
      bump();
      if (!cur.you) later(() => roll(), 900);
      return;
    }
    s.lastDoubles = false;
    let next = s.turn;
    do {
      next = (next + 1) % s.players.length;
    } while (!s.players[next].alive);
    if (s.players[next].stopped) {
      s.players[next].stopped = false;
      pushLog(`${s.players[next].name} IS PINNED ON THE DOLOSWAY — TURN LOST`, "#ff0033");
      sfx("/assets/worm-roar.wav", 0.35);
      do {
        next = (next + 1) % s.players.length;
      } while (!s.players[next].alive);
    }
    s.turn = next;
    busy.current = false;
    bump();
    if (s.players[next].you) {
      s.turnFlash = true;
      bump();
      later(() => { g.current.turnFlash = false; bump(); }, 1400);
    } else {
      later(() => roll(), 900);
    }
  };

  const moveSteps = (p, steps, done) => {
    let left = steps;
    const hop = () => {
      if (left <= 0) {
        done();
        return;
      }
      p.pos = (p.pos + 1) % SPACES.length;
      if (p.pos === 0) {
        p.scrap += GO_BONUS;
        p.food -= 1;
        if (p.food < 0) {
          p.food = 0;
          p.scrap = Math.max(0, p.scrap - STARVE_COST);
          pushLog(`${p.name} STARVES THROUGH THE LAP (-${STARVE_COST} SCRAP)`, "#8c7b75");
        }
        pushLog(`${p.name} PASSED GO CAMP (+${GO_BONUS} SCRAP, -1 RATION)`, p.color);
        s.payHit = { title: "PAYDAY // GO CAMP SALARY", sub: `${p.name} COLLECTS ${GO_BONUS} SCRAP — 1 RATION`, color: p.color, key: Date.now() };
        later(() => { g.current.payHit = null; bump(); }, 1600);
      }
      left -= 1;
      bump();
      later(hop, 190);
    };
    later(hop, 250);
  };

  const roll = () => {
    const s = g.current;
    if (busy.current || s.winner) return;
    const p = s.players[s.turn];
    if (!p.alive) return;
    busy.current = true;
    s.started = true;
    s.rolling = true;
    sfx("/assets/game-jump.wav", 0.35);
    diceClatter();
    bump();
    const d1 = rand6();
    const d2 = rand6();
    let ticks = 0;
    const anim = setInterval(() => {
      s.dice = [rand6(), rand6()];
      bump();
      ticks += 1;
      if (ticks > 7) {
        clearInterval(anim);
        s.dice = [d1, d2];
        s.rolling = false;
        if (d1 === 1 && d2 === 1) {
          p.dbl = 0;
          s.lastDoubles = false;
          const loss = Math.min(30, p.scrap);
          p.scrap -= loss;
          const lostWeapon = p.weapons > 0;
          if (lostWeapon) p.weapons -= 1;
          sfx("/assets/worm-roar.wav", 0.7);
          pushLog(`SNAKE EYES! THE WORM BURSTS FROM BELOW — ${p.name} LOSES ${loss} SCRAP${lostWeapon ? " AND A WEAPON" : ""}`, "#ff0033");
          s.wormHit = {
            title: "SNAKE EYES // THE WORM BURSTS FROM BELOW",
            sub: `${p.name} LOSES ${loss} SCRAP${lostWeapon ? " AND A WEAPON" : ""}`,
            color: p.color,
            key: Date.now(),
          };
          later(() => { g.current.wormHit = null; bump(); }, 1700);
          s.wormFlash = true;
          later(() => { g.current.wormFlash = false; bump(); }, 900);
        } else if (d1 === d2) {
          p.dbl += 1;
          s.lastDoubles = true;
          if (d1 === 6) {
            const incomplete = ["car", "home", "plane"].filter((k) => !p.builds.includes(k));
            sfx("/assets/game-coin.wav", 0.65);
            if (incomplete.length) {
              const target = incomplete.reduce((a, b) => (p.parts[a] >= p.parts[b] ? a : b));
              pushLog(`BOXCARS! SUPPLY AIR-DROP — ${p.name} CATCHES A GOLDEN ${target.toUpperCase()} PART`, "#ffd700");
              gainPart(p, target);
              s.airHit = { title: "BOXCARS // SUPPLY AIR-DROP", sub: `${p.name} CATCHES A GOLDEN ${target.toUpperCase()} PART`, color: p.color, key: Date.now() };
            } else {
              p.scrap += 30;
              pushLog(`BOXCARS! AIR-DROP CACHE — ${p.name} +30 SCRAP`, "#ffd700");
              s.airHit = { title: "BOXCARS // AIR-DROP CACHE", sub: `${p.name} +30 SCRAP`, color: p.color, key: Date.now() };
            }
            later(() => { g.current.airHit = null; bump(); }, 1700);
            s.airFlash = true;
            later(() => { g.current.airFlash = false; bump(); }, 900);
          }
          if (p.dbl >= 2) {
            p.dbl = 0;
            p.scrap += 25;
            p.weapons += 1;
            sfx("/assets/game-coin.wav", 0.6);
            pushLog(`${p.name} ROLLED ${d1} + ${d2} — DOUBLE TROUBLE BONUS! +25 SCRAP, +1 WEAPON`, "#ff9900");
          } else if (d1 !== 6) {
            pushLog(`${p.name} ROLLED DOUBLES ${d1} + ${d2} — ONE MORE DOUBLE FOR A BONUS`, p.color);
          }
        } else {
          p.dbl = 0;
          s.lastDoubles = false;
          pushLog(`${p.name} ROLLED ${d1} + ${d2}`, p.color);
        }
        bump();
        moveSteps(p, d1 + d2, () => {
          const waiting = resolveSpace(p);
          if (!waiting) endTurn();
        });
      }
    }, 90);
    timers.current.push(anim);
  };

  const reset = () => {
    g.current = freshGame(g.current.youId);
    busy.current = false;
    setTableLogged(null);
    bump();
  };

  const choose = (id) => {
    const s = g.current;
    if (s.started || s.winner || s.youId === id) return;
    g.current = freshGame(id);
    busy.current = false;
    setTableLogged(null);
    sfx("/assets/game-coin.wav", 0.35);
    pushLog(`${g.current.players.find((p) => p.you).name} ENTERS THE DOLOSWAY. ROLL THE BONES.`, "#ff0033");
    bump();
  };

  const s = g.current;
  const you = s.players.find((p) => p.you);
  const active = s.players[s.turn];
  const yourTurn = active.you && !busy.current && !s.winner;

  return (
    <div className="aboard" data-testid="apocalypse-board">
      <GameFullscreenButton testid="dolosway-fullscreen" />
      {vaultLinked ? (
        <span className="aboard-vault" data-testid="aboard-vault">
          <Crown size={11} /> CROWNS VAULTED
        </span>
      ) : null}
      {!s.started && !s.winner ? (
        <div className="aboard-pick" data-testid="aboard-pick">
          <span className="aboard-pick-label">CHOOSE YOUR RUNNER</span>
          <div className="aboard-pick-row">
            {s.players.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`aboard-pick-card ${p.you ? "is-you" : ""}`}
                style={{ "--pc": p.color }}
                onClick={() => choose(p.id)}
                data-testid={`aboard-pick-${p.id}`}
              >
                <img src={TOKENS[p.id]} alt={p.name} />
                <strong>{p.name}</strong>
                {p.you ? <em>YOU</em> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="aboard-status" data-testid="aboard-status">
        {s.players.map((p) => (
          <div
            key={p.id}
            className={`aboard-player ${p.alive ? "" : "is-out"} ${active.id === p.id && !s.winner ? "is-active" : ""}`}
            style={{ "--pc": p.color }}
            data-testid={`aboard-player-${p.id}`}
          >
            <img src={TOKENS[p.id]} alt={`${p.name} token`} />
            <div>
              <strong>
                {p.name}{p.you ? " (YOU)" : ""}
                {crowns.current[p.id] ? (
                  <em className="aboard-crowns" data-testid={`aboard-crowns-${p.id}`}>
                    <Crown size={10} /> {crowns.current[p.id]}
                  </em>
                ) : null}
              </strong>
              <span>{p.scrap} SCRAP · {p.food} FOOD · {p.weapons} WPN</span>
            </div>
          </div>
        ))}
      </div>

      <div className={`aboard-grid ${s.wormFlash ? "is-worm" : ""} ${s.airFlash ? "is-airdrop" : ""}`} data-testid="aboard-grid">
        {SPACES.map((sp, i) => {
          const [row, col] = cellOf(i);
          const owner = s.plots[i] ? s.players.find((p) => p.id === s.plots[i]) : null;
          const Icon = SPACE_ICONS[sp.t];
          const here = s.players.filter((p) => p.alive && p.pos === i);
          return (
            <div
              key={i}
              className={`aboard-cell aboard-${sp.t} ${owner ? "is-owned" : ""}`}
              style={{ gridRow: row, gridColumn: col, "--oc": owner?.color }}
              data-testid={`aboard-cell-${i}`}
            >
              <span className="aboard-cell-icon">{Icon ? <Icon size={12} /> : <b>?</b>}</span>
              <span className="aboard-cell-name">{sp.n}</span>
              {owner ? <span className="aboard-owner" style={{ background: owner.color }} /> : null}
              <span className="aboard-tokens">
                {here.map((p) => (
                  <img
                    key={p.id}
                    src={TOKENS[p.id]}
                    alt={p.name}
                    className="aboard-token"
                    style={{ borderColor: p.color }}
                  />
                ))}
              </span>
            </div>
          );
        })}

        <div className="aboard-center">
          <img className="aboard-map" src="/assets/game-bg.jpg" alt="Dolosway wasteland map" />
          <div className="aboard-center-ui">
            <span className="aboard-map-title">DOLOSWAY</span>
            <div className={`aboard-dice ${s.rolling ? "is-rolling" : ""}`} data-testid="aboard-dice">
              {s.dice.map((d, di) => (
                <span className="aboard-die" key={di}>
                  {Array.from({ length: 9 }, (_, dot) => (
                    <i key={dot} className={PIPS[d].includes(dot) ? "on" : ""} />
                  ))}
                </span>
              ))}
            </div>
            <button
              type="button"
              className="aboard-roll"
              onClick={roll}
              disabled={!yourTurn}
              data-testid="aboard-roll"
            >
              <Dices size={18} />
              {s.winner ? "GAME OVER" : yourTurn ? "ROLL THE BONES" : busy.current || !active.you ? `${active.name} MOVES…` : "ROLL"}
            </button>
          </div>
        </div>

        {s.deathHit && !s.winner ? (
          <div className="aboard-deathhit" key={s.deathHit.key} data-testid="aboard-deathhit">
            <div className="aboard-deathhit-card">
              <Skull size={56} />
            </div>
            <div className="aboard-deathhit-msg">
              <strong>RUNNER DOWN // {s.deathHit.name}</strong>
              <span style={{ color: s.deathHit.color }}>STARVED OUT — PLOTS RELEASED. THE STRONGEST SURVIVE.</span>
            </div>
          </div>
        ) : null}

        {s.payHit && !s.winner ? (
          <div className="aboard-payhit" key={s.payHit.key} data-testid="aboard-payhit">
            <div className="aboard-payhit-card">
              <Flag size={56} />
            </div>
            <div className="aboard-payhit-msg">
              <strong>{s.payHit.title}</strong>
              <span style={{ color: s.payHit.color }}>{s.payHit.sub}</span>
            </div>
          </div>
        ) : null}

        {s.campHit && !s.winner ? (
          <div className="aboard-camphit" key={s.campHit.key} data-testid="aboard-camphit">
            <div className="aboard-camphit-card">
              <Tent size={56} />
            </div>
            <div className="aboard-camphit-msg">
              <strong>{s.campHit.title}</strong>
              <span style={{ color: s.campHit.color }}>{s.campHit.sub}</span>
            </div>
          </div>
        ) : null}

        {s.chanceHit && !s.winner ? (
          <div className="aboard-chancehit" key={s.chanceHit.key} data-testid="aboard-chancehit">
            <div className="aboard-chancehit-card">
              <div className="aboard-chancehit-inner">
                <div className="aboard-chancehit-face is-back">
                  <Dices size={42} />
                  <em>WASTE CARD</em>
                </div>
                <div className="aboard-chancehit-face is-front">
                  <s.chanceHit.icon size={42} />
                  <em>{s.chanceHit.sub}</em>
                </div>
              </div>
            </div>
            <div className="aboard-chancehit-msg">
              <strong>{s.chanceHit.title}</strong>
            </div>
          </div>
        ) : null}

        {s.supplyHit && !s.winner ? (
          <div className="aboard-supplyhit" key={s.supplyHit.key} data-testid="aboard-supplyhit">
            <div className="aboard-supplyhit-card">
              <Package size={64} />
            </div>
            <div className="aboard-supplyhit-msg">
              <strong>{s.supplyHit.title}</strong>
              <span style={{ color: s.supplyHit.color }}>{s.supplyHit.sub}</span>
            </div>
          </div>
        ) : null}

        {s.clashHit && !s.winner ? (
          <div className="aboard-clashhit" key={s.clashHit.key} data-testid="aboard-clashhit">
            <div className="aboard-clashhit-msg">
              <strong>{s.clashHit.title}</strong>
              <span style={{ color: s.clashHit.color }}>{s.clashHit.sub}</span>
            </div>
            <div className="aboard-clashhit-card">
              <Swords size={64} />
            </div>
          </div>
        ) : null}

        {s.airHit && !s.winner ? (
          <div className="aboard-airhit" key={s.airHit.key} data-testid="aboard-airhit">
            <div className="aboard-airhit-card">
              <Zap size={64} />
            </div>
            <div className="aboard-airhit-msg">
              <strong>{s.airHit.title}</strong>
              <span style={{ color: s.airHit.color }}>{s.airHit.sub}</span>
            </div>
          </div>
        ) : null}

        {s.wormHit && !s.winner ? (
          <div className="aboard-wormhit" key={s.wormHit.key} data-testid="aboard-wormhit">
            <div className="aboard-wormhit-card">
              <img src="/assets/board-worm-card.jpg" alt="The Sand King worm attacks" />
            </div>
            <div className="aboard-wormhit-msg">
              <strong>{s.wormHit.title}</strong>
              <span style={{ color: s.wormHit.color }}>{s.wormHit.sub}</span>
            </div>
          </div>
        ) : null}

        {s.turnFlash && !s.winner ? (
          <div className="aboard-turnflash" style={{ "--pc": you.color }} data-testid="aboard-turnflash">
            <span>YOUR TURN</span>
          </div>
        ) : null}

        {s.hwyChoice && !s.winner ? (
          <div className="aboard-hwychoice" key={s.hwyChoice.key} data-testid="aboard-hwychoice">
            <div className="aboard-hwychoice-card">
              <Crosshair size={54} />
            </div>
            <div className="aboard-hwychoice-msg">
              <strong>DOLOSWAY // TAKE THE SHOT?</strong>
              <span>
                SPEND 1 WEAPON TO PIN {s.players.find((r) => r.id === s.hwyChoice.targetId)?.name} (IN THE LEAD) — THEY LOSE A TURN, YOU TAKE +5 SCRAP. {s.hwyChoice.left} SHOT{s.hwyChoice.left === 1 ? "" : "S"} LEFT THIS GAME.
              </span>
              <div className="aboard-hwychoice-btns">
                <button type="button" onClick={() => resolveHwy(true)} data-testid="aboard-hwy-fire">FIRE — +5 SCRAP</button>
                <button type="button" className="is-holster" onClick={() => resolveHwy(false)} data-testid="aboard-hwy-holster">HOLSTER</button>
              </div>
            </div>
          </div>
        ) : null}

        {s.hwyHit && !s.winner ? (
          <div className="aboard-clashhit" key={s.hwyHit.key} data-testid="aboard-hwyhit">
            <div className="aboard-clashhit-msg">
              <strong>{s.hwyHit.title}</strong>
              <span style={{ color: s.hwyHit.color }}>{s.hwyHit.sub}</span>
            </div>
            <div className="aboard-clashhit-card">
              <Crosshair size={64} />
            </div>
          </div>
        ) : null}

        {s.winner ? (
          <div className="aboard-winner" data-testid="aboard-winner" style={{ "--pc": s.winner.color }}>
            <img
              className="aboard-winner-art"
              src={ART[s.winner.id]}
              alt={`${s.winner.name} — Warlord of the Wastes`}
              data-testid="aboard-winner-art"
            />
            <strong><Trophy size={20} /> {s.winner.name} IS WARLORD OF THE WASTES</strong>
            <span>{s.winner.builds.length === 3 ? "CAR + HOME + PLANE FORTIFIED" : "LAST RUNNER STANDING"}</span>
            <div className="daily-submit aboard-table-log" data-testid="table-submit">
              <span>WEEKLY TABLE POT — LOG YOUR GAME</span>
              {tableLogged ? (
                <p className="daily-logged" data-testid="table-logged">
                  <Trophy size={13} /> LOGGED — RANK #{tableLogged.rank} THIS WEEK · POT NOW {tableLogged.pot} PTS
                </p>
              ) : (
                <div className="daily-submit-row">
                  <input
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                    maxLength={24}
                    placeholder="CALLSIGN"
                    data-testid="table-callsign-input"
                  />
                  <button type="button" className="button button-primary" onClick={logTable} disabled={tableBusy} data-testid="table-log-button">
                    {tableBusy ? "LOGGING…" : "LOG TABLE"}
                  </button>
                </div>
              )}
            </div>
            <button type="button" onClick={reset} data-testid="aboard-reset">
              <RotateCcw size={14} /> NEW GAME
            </button>
          </div>
        ) : null}
      </div>

      <div className="aboard-loadout" data-testid="aboard-loadout">
        {[
          { k: "car", label: "CAR", Icon: Car, count: you.parts.car },
          { k: "home", label: "HOME", Icon: Home, count: you.parts.home },
          { k: "plane", label: "PLANE", Icon: Plane, count: you.parts.plane },
        ].map(({ k, label, Icon, count }) => (
          <div key={k} className={`aboard-build ${you.builds.includes(k) ? "is-built" : ""}`} data-testid={`aboard-build-${k}`}>
            <Icon size={16} />
            <span>{label}</span>
            <div className="aboard-pips">
              {[0, 1, 2].map((pip) => (
                <i key={pip} className={pip < count ? "on" : ""} />
              ))}
            </div>
          </div>
        ))}
        <div className="aboard-build is-static">
          <Apple size={16} />
          <span>{you.food} FOOD</span>
        </div>
        <div className="aboard-build is-static">
          <Sword size={16} />
          <span>{you.weapons} WPN</span>
        </div>
      </div>

      <div className="daily-challenge aboard-pot" data-testid="table-pot">
        <div className="daily-challenge-head">
          <span className="eyebrow">WEEKLY TABLE POT // BOARD BOSS CROWN</span>
          <strong data-testid="table-pot-size">{potInfo ? `${potInfo.pot} PTS POT · ${potInfo.games} ${potInfo.games === 1 ? "TABLE" : "TABLES"} THIS WEEK` : "THE POT IS BUILDING"}</strong>
          {potInfo?.champion ? (
            <span className="daily-yesterday" data-testid="table-champion">
              <Trophy size={12} /> LAST WEEK'S BOARD BOSS — {potInfo.champion.name} · {potInfo.champion.wins} {potInfo.champion.wins === 1 ? "WIN" : "WINS"} · TOOK {potInfo.champion.pot} PTS
            </span>
          ) : null}
          <p>Every logged table feeds the pot (30 PTS base, +2 per game). Wins outrank scrap — Sunday 18:10 UTC the table leader takes the whole pot.</p>
        </div>
        {potInfo?.rows?.length ? (
          <div className="daily-board" data-testid="table-pot-rows">
            {potInfo.rows.slice(0, 3).map((row, i) => (
              <div className={`daily-board-row${i === 0 ? " is-leader" : ""}`} key={row.name} data-testid={`table-pot-row-${i + 1}`}>
                <span>{["1ST", "2ND", "3RD"][i]}</span>
                <strong>{row.name}</strong>
                <em>{row.wins} {row.wins === 1 ? "WIN" : "WINS"} · BEST {row.best_scrap} SCRAP</em>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className={`aboard-rules-toggle ${rulesOpen ? "is-open" : ""}`}
        onClick={() => setRulesOpen((v) => !v)}
        data-testid="aboard-rules-toggle"
      >
        TABLE STAKES // HOW TO PLAY
        <ChevronDown size={14} />
      </button>

      {rulesOpen ? (
        <div className="aboard-rules" data-testid="aboard-rules">
          {[
            { Icon: Flag, term: "CLAIM PLOTS", desc: "20 scrap to claim. Rivals landing on your plot pay you 15 toll — you keep it." },
            { Icon: Dices, term: "DOUBLES", desc: "Any doubles = roll again. Momentum is survival." },
            { Icon: Crown, term: "DOUBLE TROUBLE", desc: "2 doubles in a row: +25 scrap and +1 weapon on top of the extra turn." },
            { Icon: Skull, term: "SNAKE EYES (1+1)", desc: "The worm bursts from below: lose 30 scrap and a weapon. No extra turn." },
            { Icon: Package, term: "BOXCARS (6+6)", desc: "Supply air-drop: a golden part lands in your most-advanced build." },
            { Icon: Tent, term: "GO CAMP", desc: "Passing pays 30 scrap but eats 1 ration. Hit 0 scrap and you starve out." },
            { Icon: Crosshair, term: "DOLOSWAY SHOT", desc: "Land on DOLOSWAY armed and you can fire on the runner in the lead: they lose their next turn, you take +5 scrap. Costs 1 weapon — only 3 shots per game." },
            { Icon: Trophy, term: "WARLORD", desc: "Fortify CAR + HOME + PLANE — or simply outlive every rival." },
            { Icon: Crown, term: "WEEKLY POT", desc: "Every logged table feeds the pot (+2 PTS each, 30 base, cap 150). Sunday 18:10 UTC the week's win-leader takes it all — link your Titan Tag to collect." },
          ].map(({ Icon, term, desc }) => (
            <div key={term} className="aboard-rule">
              <Icon size={14} />
              <div>
                <strong>{term}</strong>
                <span>{desc}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="aboard-log" data-testid="aboard-log">
        {s.log.map((entry) => (
          <p key={entry.id} style={{ color: entry.color }}>{entry.line}</p>
        ))}
      </div>
    </div>
  );
}
